/**
 * InfoTip -- renders a Tooltip for short content and upgrades to a
 * hover-controlled, scrollable HoverCard when content is long or rich JSX.
 *
 * Uses the same Ark UI primitives as Tooltip and HoverCard wrappers.
 */

import { type ReactNode, type ReactElement } from 'react';
import { HoverCard as ArkHoverCard, Portal } from '@ark-ui/react';
import { TooltipPrimitive } from './TooltipPrimitive';

/** Character count above which string content is promoted to a hover card. */
export const INFOTIP_CHAR_THRESHOLD = 120;

/** Grace period (ms) when the pointer leaves trigger/card before closing. */
const HOVER_CLOSE_DELAY = 150;

export interface InfoTipProps {
  /** The content to display */
  content: ReactNode;
  /** The element that triggers the tip (must be a single element) */
  children: ReactElement;
  /** Position relative to the trigger */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Delay in ms before showing (tooltip mode only, default: 200) */
  delay?: number;
  /** Extra CSS class on the content container */
  className?: string;
  /** Force a specific rendering mode instead of auto-detecting */
  mode?: 'tooltip' | 'popover' | 'auto';
}

function getContentLength(content: ReactNode): number | null {
  if (typeof content === 'string') return content.length;
  if (typeof content === 'number') return String(content).length;
  return null;
}

export function shouldUsePopover(
  content: ReactNode,
  mode: 'tooltip' | 'popover' | 'auto',
): boolean {
  if (mode === 'tooltip') return false;
  if (mode === 'popover') return true;
  const len = getContentLength(content);
  if (len === null) return true;
  return len > INFOTIP_CHAR_THRESHOLD;
}

export function InfoTip({
  content,
  children,
  position = 'top',
  delay = 200,
  className = '',
  mode = 'auto',
}: InfoTipProps) {
  const usePopover = shouldUsePopover(content, mode);

  if (!usePopover) {
    return (
      <TooltipPrimitive
        content={content}
        position={position}
        delay={delay}
        className={className}
      >
        {children}
      </TooltipPrimitive>
    );
  }

  return (
    <HoverPopover
      content={content}
      position={position}
      delay={delay}
      className={className}
    >
      {children}
    </HoverPopover>
  );
}

/**
 * Ark HoverCard so long copy stays open while the pointer moves into the
 * card (scroll, selection) without the click/focus cycle of a Popover —
 * which retriggers inline links after close.
 */
function HoverPopover({
  content,
  children,
  position,
  delay,
  className,
}: Omit<InfoTipProps, 'mode'>) {
  return (
    <ArkHoverCard.Root
      openDelay={delay ?? 200}
      closeDelay={HOVER_CLOSE_DELAY}
      positioning={{ placement: position, gutter: 8 }}
    >
      <ArkHoverCard.Trigger asChild>{children}</ArkHoverCard.Trigger>
      <Portal>
        <ArkHoverCard.Positioner className="infotip-popover-positioner">
          <ArkHoverCard.Content
            className={`popover-content infotip-popover ${className}`.trim()}
          >
            <ArkHoverCard.Arrow className="popover-arrow">
              <ArkHoverCard.ArrowTip className="popover-arrow-tip" />
            </ArkHoverCard.Arrow>
            <div className="infotip-popover-body">{content}</div>
          </ArkHoverCard.Content>
        </ArkHoverCard.Positioner>
      </Portal>
    </ArkHoverCard.Root>
  );
}
